import { Participant, RoomEvent } from 'livekit-client';
import { useEffect, useRef, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';

export type AgentState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'waiting';

interface UseAgentStateReturn {
  agentState: AgentState;
  isAgentSpeaking: boolean;
  isUserSpeaking: boolean;
}

const SPEAKING_DEBOUNCE_MS = 700;

export function useAgentState(): UseAgentStateReturn {
  const room = useRoomContext();
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [lastActiveState, setLastActiveState] = useState<'listening' | 'speaking' | null>(null);

  const agentSpeakingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userSpeakingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleActiveSpeakersChanged = (speakers: Participant[]) => {
      const agentIsSpeaking = speakers.some(speaker => !speaker.isLocal);
      const userIsSpeaking = speakers.some(speaker => speaker.isLocal);

      // Agent speaking state with debounce
      if (agentIsSpeaking) {
        if (agentSpeakingTimeoutRef.current) {
          clearTimeout(agentSpeakingTimeoutRef.current);
          agentSpeakingTimeoutRef.current = null;
        }
        setIsAgentSpeaking(true);
      } else {
        if (!agentSpeakingTimeoutRef.current) {
          agentSpeakingTimeoutRef.current = setTimeout(() => {
            setIsAgentSpeaking(false);
            agentSpeakingTimeoutRef.current = null;
          }, SPEAKING_DEBOUNCE_MS);
        }
      }

      // User speaking state with debounce
      if (userIsSpeaking) {
        if (userSpeakingTimeoutRef.current) {
          clearTimeout(userSpeakingTimeoutRef.current);
          userSpeakingTimeoutRef.current = null;
        }
        setIsUserSpeaking(true);
      } else {
        if (!userSpeakingTimeoutRef.current) {
          userSpeakingTimeoutRef.current = setTimeout(() => {
            setIsUserSpeaking(false);
            userSpeakingTimeoutRef.current = null;
          }, SPEAKING_DEBOUNCE_MS);
        }
      }
    };

    room.on(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakersChanged);

    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakersChanged);
      if (agentSpeakingTimeoutRef.current) {
        clearTimeout(agentSpeakingTimeoutRef.current);
      }
      if (userSpeakingTimeoutRef.current) {
        clearTimeout(userSpeakingTimeoutRef.current);
      }
    };
  }, [room]);

  // Track last active speaker for thinking/waiting derivation
  useEffect(() => {
    if (isAgentSpeaking) {
      setLastActiveState('speaking');
    } else if (isUserSpeaking) {
      setLastActiveState('listening');
    }
  }, [isAgentSpeaking, isUserSpeaking]);

  // Derive agent state
  let agentState: AgentState;
  if (isAgentSpeaking) {
    agentState = 'speaking';
  } else if (isUserSpeaking) {
    agentState = 'listening';
  } else if (lastActiveState === 'listening') {
    agentState = 'thinking';
  } else if (lastActiveState === 'speaking') {
    agentState = 'waiting';
  } else {
    agentState = 'idle';
  }

  return { agentState, isAgentSpeaking, isUserSpeaking };
}
